import React from 'react';
import {BUNDLE_ICON} from '../../Constants';
import {PREPARED_CART_ICON} from '../../Constants';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../store/rootReducer';
import useCan, { ORG_EXTENSION_MODIFY, ORG_MODIFY, SEARCH, STOREFRONT_ADMIN, TAX_CODE_MODIFY } from '../../permissions/ability';
import { useVisibleSettings } from '../../hooks/useVisibleSettings';
import { findArea } from '../../settings/registry';
import {
    useGetAllVendorSettingsQuery,
    useGetContactsQuery,
    useGetInstalledExtensionsQuery,
    useGetQuicksearchQuery,
    useGetTaxCodesQuery,
    useSearchCompanyRefsQuery,
    useSearchWorkspaceRefsQuery,
    useSetSelectedWorkspaceMutation,
} from '../../store/Api';
import { useGetOrganizationQuery } from '../../store/endpoints/OrganizationsEndpoints';
import {
    useSearchProductCategoryRefsQuery,
    useSearchProductSegmentRefsQuery,
} from '../../store/endpoints/ProductCategorizationEndpoints';
import { useGetSavedSearchRefsQuery } from '../../store/endpoints/SavedSearchEndpoints';
import { toast } from 'react-toastify';
import {
    useGetStorefrontsQuery,
    useLazyGetStorefrontLoginLinkQuery,
    useResetStorefrontCacheSeedMutation,
    useSearchStorefrontCartRefsQuery,
    useSearchStorefrontOrderRefsQuery,
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import { SESSION_ERROR, sessionErrorFrom } from '../Storefront/storefrontSessionError';
import { useSearchPreparedCartRefsQuery } from '../../store/endpoints/PreparedCartEndpoints';
import { useGetTopProductBundleRefsQuery } from '../../store/endpoints/ProductBundleEndpoints';
import { updateSearchCriteria, updateSelectedSaveSearchId } from '../../store/searchSlice';
import { setOpenBundleId } from '../../store/settingsSlice';
import { useSearchActions } from './useSearchActions';
import { expandAttributeBoosts, parseProductQuery } from './productQueryParser';
import { buildFacetPreviewCriteria } from './useProductQueryIntel';
import { useGetBundleProductSuggestionsQuery, useGetProductSearchPreviewQuery, useGetSearchFacetPreviewQuery, useGetSearchSuggestionsQuery, useGetWorkspaceProductSuggestionsQuery } from '../../store/Api';
import { addProductToDestination } from '../../logic/addToDestination';
import { IProductSuggestion } from '../../models/Workspace';
import { generateShortDescription } from '../../logic/Product';
import { INormalizedProduct } from '../../models/NormalizedProduct';
import {
    findNameScope,
    IGlobalSearchHit,
    ISectionArgs,
    ISectionResult,
    matchRank,
    MIN_SERVER_TERM,
    parseSectionHint,
    RANK_LABEL_PREFIX,
    RANK_LABEL_SUBSTRING,
    SectionKey,
    sortHits,
    IHitPreview,
    contextDestination,
} from './sections';

const CAP_ACTIONS = 5;
const CAP_SETTINGS = 5;
const CAP_ENTITY = 3;
const CAP_PRODUCTS = 5;
/** Workspace context leans in: more real products per query, and a fuller suggestion list before typing. */
const CAP_CONTEXT_PRODUCTS = 8;
const CAP_CONTEXT_SUGGESTIONS = 10;
const COST_FORMAT = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
/** "Cost $12.30" for one price, "Cost $12.30–$15.00" for a spread, undefined when nothing is priced. */
const formatCostRange = (lowest?: number, highest?: number): string | undefined => {
    if (lowest === undefined || lowest === null) return undefined;
    if (highest === undefined || highest === null || Math.abs(highest - lowest) < 0.005) return `Cost ${COST_FORMAT.format(lowest)}`;
    return `Cost ${COST_FORMAT.format(lowest)}–${COST_FORMAT.format(highest)}`;
};
/** Vendor cost spread for a search result, in-stock vendors preferred (mirrors the API's suggestion range). */
const costRangeOf = (vendors?: { cost?: number; inStock?: boolean }[]): { lowest?: number; highest?: number; inStockVendors: number } => {
    const priced = (vendors ?? []).filter((v) => (v.cost ?? 0) > 0);
    const inStock = priced.filter((v) => v.inStock);
    const pool = inStock.length > 0 ? inStock : priced;
    if (pool.length === 0) return { inStockVendors: inStock.length };
    const costs = pool.map((v) => v.cost as number);
    return { lowest: Math.min(...costs), highest: Math.max(...costs), inStockVendors: inStock.length };
};
const trimDescription = (text?: string): string | undefined => {
    if (!text) return undefined;
    const clean = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!clean) return undefined;
    return clean.length > 160 ? `${clean.slice(0, 157).trimEnd()}…` : clean;
};
const SUGGESTION_ICONS: Record<IProductSuggestion['reason'], string> = {
    boughtTogether: 'ShoppingCart',
    bundledTogether: BUNDLE_ICON,
    offeredTogether: 'WorkItem',
    storefrontTopSeller: 'Shop',
    oftenAdded: 'FavoriteStar',
    topSeller: 'Trending12'
};

const useActionsSection = (args: ISectionArgs): ISectionResult => {
    const hits = useSearchActions(args.term, args.openEntity);
    return {
        section: 'actions',
        label: 'Actions',
        isLoading: false,
        hits: sortHits([...hits], CAP_ACTIONS),
    };
};

const CAP_FREQUENT = 8;

/**
 * The org's frequently searched terms fill the otherwise empty palette: one click re-runs the
 * search. Backed by the search-log aggregation (human traffic only - API keys and facet-preview
 * probes are excluded server-side); shares its RTK entry with the palette's popularity weighting.
 */
const useFrequentSearchesSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const dispatch = useDispatch();
    const can = useCan(SEARCH);
    const enabled = args.isOpen && can && args.term === '';
    const suggestions = useGetSearchSuggestionsQuery(undefined, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!enabled || !suggestions.data) return [];
        const emptyCriteria = {
            keywords: '', page: 1, pageSize: 25, inStock: true,
            manufacturer: [], vendor: [], must: [], boost: [], exclude: [], filters: [],
        };
        const makeRun = (criteria: object) => () => {
            dispatch(updateSearchCriteria({ ...emptyCriteria, ...criteria }));
            dispatch(updateSelectedSaveSearchId(undefined));
            history.push('/search');
        };
        const rows: IGlobalSearchHit[] = [];
        for (const t of suggestions.data.keywords.slice(0, 4)) {
            rows.push({
                section: 'frequent', key: `kw-${t.value}`, label: t.value,
                secondary: `${t.count} recent searches`, icon: 'History',
                rank: -t.score, run: makeRun({ keywords: t.value }),
            });
        }
        for (const t of suggestions.data.categories.slice(0, 2)) {
            rows.push({
                section: 'frequent', key: `cat-${t.value}`, label: `Browse ${t.value}`,
                secondary: 'Frequently searched category', icon: 'ProductVariant',
                rank: -t.score, run: makeRun({ category: t.value }),
            });
        }
        for (const t of suggestions.data.manufacturers.slice(0, 2)) {
            rows.push({
                section: 'frequent', key: `mfr-${t.value}`, label: `Shop ${t.value}`,
                secondary: 'Frequently searched brand', icon: 'Shop',
                rank: -t.score, run: makeRun({ manufacturer: [t.value] }),
            });
        }
        return rows.sort((a, b) => a.rank - b.rank).slice(0, CAP_FREQUENT);
    }, [enabled, suggestions.data, dispatch, history]);

    return {
        section: 'frequent',
        label: 'Frequent Searches',
        isLoading: enabled && suggestions.isLoading,
        hits,
    };
};

const useSettingsSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const { visibleAreas, visibleSettings, claimsLoaded } = useVisibleSettings();

    const hits = React.useMemo(() => {
        if (!args.term || !claimsLoaded) return [];
        const out: IGlobalSearchHit[] = [];
        for (const setting of visibleSettings) {
            const rank = matchRank(args.term, setting.label, setting.id, setting.description);
            if (rank === undefined) continue;
            out.push({
                section: 'settings',
                key: `setting-${setting.id}`,
                label: setting.label,
                secondary: findArea(setting.area)?.label,
                icon: 'Settings',
                rank,
                // The deep-link resolver owns tab/panel restoration and the focus ring.
                run: () => history.push(`/go/setting/${encodeURIComponent(setting.id)}`),
            });
        }
        for (const area of visibleAreas) {
            const rank = matchRank(args.term, area.label, area.key, area.description);
            if (rank === undefined) continue;
            out.push({
                section: 'settings',
                key: `area-${area.key}`,
                label: area.label,
                secondary: 'Screen',
                icon: 'Nav2DMapView',
                rank,
                run: () => history.push(`/go/area/${encodeURIComponent(area.key)}`),
            });
        }
        return sortHits(out, CAP_SETTINGS);
    }, [args.term, claimsLoaded, visibleSettings, visibleAreas, history]);

    return { section: 'settings', label: 'Settings', isLoading: false, hits };
};

const useStorefrontsSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const can = useCan(STOREFRONT_ADMIN);
    const enabled = args.isOpen && can;
    // Unpaged and RTK-cached org-wide; a client-side filter over it is the established pattern
    // (settings search scopes instance names against this same subscription).
    const storefronts = useGetStorefrontsQuery(undefined, { skip: !enabled });
    const [getStorefrontLink] = useLazyGetStorefrontLoginLinkQuery();
    const [resetCacheSeed] = useResetStorefrontCacheSeedMutation();
    const { visibleAreas } = useVisibleSettings();

    const hits = React.useMemo(() => {
        if (!args.term || !enabled) return [];
        // Browse mode: asking for the section by name lists every storefront - nobody
        // remembers a customer org's storefront names on login.
        const browsing = ['storefronts', 'storefront', 'stores', 'all storefronts', 'list storefronts', 'my storefronts'].includes(args.term);
        const out: IGlobalSearchHit[] = [];
        const previewRows: IGlobalSearchHit[] = [];
        const scopedRows: IGlobalSearchHit[] = [];
        for (const storefront of storefronts.data?.items ?? []) {
            if (!storefront.id) continue;
            const label = storefront.name || storefront.subUrl || storefront.id;
            const rank = browsing ? RANK_LABEL_PREFIX : matchRank(args.term, label, storefront.subUrl);
            if (rank !== undefined) {
                out.push({
                    section: 'storefronts',
                    key: storefront.id,
                    label,
                    secondary: storefront.subUrl,
                    icon: 'Shop',
                    rank,
                    run: () => history.push(`/go/area/storefront.general?instance=${encodeURIComponent(storefront.id ?? '')}`),
                });
            }
            // "Preview {name} storefront" mints the admin login link for the draft (-preview)
            // sub URL and opens it in a new tab — the same flow as the storefronts table's
            // Preview button. Matching against the full label means typing "preview" alone
            // lists these rows, and "preview warp" ranks the intended store first.
            // "Clear {name} storefront cache" resets the storefront's cache seed — the same call
            // as the storefront title bar's Clear Cache button. Matched like the preview rows:
            // label prefix, or every query token present in the label ("clear cache warp").
            const cacheSubUrl = storefront.subUrl ?? storefront.liveSubUrl;
            if (cacheSubUrl) {
                const clearLabel = `Clear ${label} storefront cache`;
                const lowerClear = clearLabel.toLowerCase();
                const clearRank = lowerClear.startsWith(args.term) ? 0
                    : args.term.split(/\s+/).every((t) => lowerClear.includes(t)) ? RANK_LABEL_SUBSTRING
                    : undefined;
                if (clearRank !== undefined) {
                    previewRows.push({
                        section: 'storefronts',
                        key: `clear-${storefront.id}`,
                        label: clearLabel,
                        secondary: 'Resets this storefront cache',
                        icon: 'Refresh',
                        rank: clearRank,
                        run: () => {
                            resetCacheSeed({ subUrl: cacheSubUrl })
                                .unwrap()
                                .then(() => toast.success('Cache cleared successfully'))
                                .catch(() => toast.error('Failed to clear cache'));
                        },
                    });
                }
            }
            const liveSubUrl = storefront.liveSubUrl ?? storefront.subUrl;
            if (liveSubUrl) {
                const previewLabel = `Preview ${label} storefront`;
                const previewRank = matchRank(args.term, previewLabel, storefront.subUrl);
                if (previewRank !== undefined) {
                    previewRows.push({
                        section: 'storefronts',
                        key: `preview-${storefront.id}`,
                        label: previewLabel,
                        secondary: 'Opens the storefront preview in a new tab',
                        icon: 'View',
                        rank: previewRank,
                        run: () => {
                            getStorefrontLink(`${liveSubUrl}-preview`)
                                .unwrap()
                                .then((resp) => {
                                    if (resp.redirectUrl) {
                                        window.open(resp.redirectUrl, '_blank');
                                    } else {
                                        toast.error(SESSION_ERROR);
                                    }
                                })
                                .catch((error) => toast.error(sessionErrorFrom(error)));
                        },
                    });
                }
            }
        }
        // Compound query — "warp speed orders": a token window naming a storefront plus remaining
        // tokens naming one of its screens opens that screen scoped to the store (dashboard tabs,
        // pages editor, general settings, …) through the deep-link resolver.
        const tokens = args.term.split(/\s+/).filter(Boolean);
        const storefrontOptions = (storefronts.data?.items ?? [])
            .map((s) => ({ id: s.id ?? '', label: s.name || s.subUrl || (s.id ?? '') }))
            .filter((o) => o.id);
        const scope = findNameScope(tokens, storefrontOptions);
        if (scope) {
            const remaining = [...tokens.slice(0, scope.tokenStart), ...tokens.slice(scope.tokenEnd)].join(' ');
            if (remaining) {
                for (const area of visibleAreas) {
                    if (area.instanceType !== 'storefront') continue;
                    const shortLabel = area.label.replace(/^Storefront · /, '');
                    const rank = matchRank(remaining, shortLabel, area.key);
                    if (rank === undefined) continue;
                    scopedRows.push({
                        section: 'storefronts',
                        key: `scoped-${scope.id}-${area.key}`,
                        label: `${scope.label} · ${shortLabel}`,
                        secondary: 'Storefront screen',
                        icon: 'Nav2DMapView',
                        rank,
                        run: () => history.push(`/go/area/${encodeURIComponent(area.key)}?instance=${encodeURIComponent(scope.id)}`),
                    });
                }
            }
        }
        // Scoped screen rows first (the most specific intent), then plain storefront rows, then
        // preview rows - capped separately so no group crowds out another. Browse mode asked for
        // the whole list by name, so it gets the whole list.
        return [...sortHits(scopedRows, 4), ...sortHits(out, browsing ? Infinity : CAP_ENTITY), ...sortHits(previewRows, CAP_ENTITY * 2)];
    }, [args.term, enabled, storefronts.data, visibleAreas, history, getStorefrontLink, resetCacheSeed]);

    return { section: 'storefronts', label: 'Storefronts', isLoading: args.term !== '' && enabled && storefronts.isLoading, hits };
};

const useWorkspacesSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const [setSelectedWorkspace] = useSetSelectedWorkspaceMutation();
    const enabled = args.isOpen && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const workspaces = useSearchWorkspaceRefsQuery({ searchText: args.debouncedTerm, pageSize: CAP_ENTITY }, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!enabled) return [];
        return (workspaces.data ?? []).slice(0, CAP_ENTITY).map((ws): IGlobalSearchHit => ({
            section: 'workspaces',
            key: ws.id ?? '',
            label: ws.name ?? ws.id ?? '',
            icon: 'WorkItem',
            rank: RANK_LABEL_SUBSTRING,
            run: () => {
                setSelectedWorkspace(ws.id ?? '');
                history.push(`/workspace/${ws.id}`);
            },
        }));
    }, [enabled, workspaces.data, setSelectedWorkspace, history]);

    return { section: 'workspaces', label: 'Workspaces', isLoading: enabled && workspaces.isFetching, hits };
};

const useContactsSection = (args: ISectionArgs): ISectionResult => {
    const enabled = args.isOpen && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const contacts = useGetContactsQuery(
        {
            query: { query: args.debouncedTerm, conditions: { page: 1, pageSize: CAP_ENTITY, searchTerm: args.debouncedTerm } },
            includeCompanyDetails: false,
        },
        { skip: !enabled }
    );

    const hits = React.useMemo(() => {
        if (!enabled) return [];
        return (contacts.data?.items ?? []).slice(0, CAP_ENTITY).map((contact): IGlobalSearchHit => ({
            section: 'contacts',
            key: contact.id ?? contact.emailAddress,
            label: `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || contact.emailAddress,
            // Contacts match on their company too, so show it - a hit that only makes sense
            // through its company otherwise looks random.
            secondary: [contact.emailAddress, contact.companyName].filter(Boolean).join(' \u00b7 '),
            icon: 'Contact',
            rank: RANK_LABEL_SUBSTRING,
            // Contact management has no URL-panel contract, so open the details modal in place.
            run: () => args.openEntity({ kind: 'contact', contact }),
        }));
    }, [enabled, contacts.data, args.openEntity]);

    return { section: 'contacts', label: 'Contacts', isLoading: enabled && contacts.isFetching, hits };
};

const useCompaniesSection = (args: ISectionArgs): ISectionResult => {
    const enabled = args.isOpen && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const companies = useSearchCompanyRefsQuery({ searchTerm: args.debouncedTerm, pageSize: CAP_ENTITY }, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!enabled) return [];
        return (companies.data?.items ?? []).slice(0, CAP_ENTITY)
            .filter((company) => company.id)
            .map((company): IGlobalSearchHit => ({
                section: 'companies',
                key: company.id ?? '',
                label: company.companyName ?? company.id ?? '',
                secondary: company.website,
                icon: 'CityNext2',
                rank: RANK_LABEL_SUBSTRING,
                run: () => args.openEntity({ kind: 'company', companyId: company.id ?? '' }),
            }));
    }, [enabled, companies.data, args.openEntity]);

    return { section: 'companies', label: 'Companies', isLoading: enabled && companies.isFetching, hits };
};

const useExtensionsSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const can = useCan(ORG_EXTENSION_MODIFY);
    const enabled = args.isOpen && can;
    const installed = useGetInstalledExtensionsQuery(undefined, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!args.term || !enabled) return [];
        const out: IGlobalSearchHit[] = [];
        for (const extension of installed.data ?? []) {
            const label = extension.nickname || extension.name;
            const rank = matchRank(args.term, label, extension.name);
            if (rank === undefined) continue;
            out.push({
                section: 'extensions',
                key: extension.id,
                label,
                secondary: extension.extensionType === 'Folio' ? 'Folio' : extension.extensionType,
                icon: 'AppIconDefault',
                rank,
                // Extension Management resolves whether the record needs the folio panel itself,
                // so ?panel=extension is safe for folios and sub-extensions alike.
                run: () => history.push(`/settings/extensions?panel=extension&instance=${encodeURIComponent(extension.id)}`),
            });
        }
        return sortHits(out, CAP_ENTITY);
    }, [args.term, enabled, installed.data, history]);

    return { section: 'extensions', label: 'Extensions', isLoading: args.term !== '' && enabled && installed.isLoading, hits };
};

const useUsersSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    // Users ride on the org record NavMenu already subscribes to — no extra network.
    const organization = useGetOrganizationQuery(selectedOrganization ?? '', { skip: !args.isOpen || !selectedOrganization });

    const hits = React.useMemo(() => {
        if (!args.term || !args.isOpen) return [];
        const out: IGlobalSearchHit[] = [];
        for (const user of organization.data?.users ?? []) {
            const rank = matchRank(args.term, user.username);
            if (rank === undefined) continue;
            out.push({
                section: 'users',
                key: user.username,
                label: user.username,
                secondary: user.role,
                icon: 'Contact',
                rank,
                // Users have no details deep link or standalone panel — land on the list.
                run: () => history.push('/settings/users'),
            });
        }
        return sortHits(out, CAP_ENTITY);
    }, [args.term, args.isOpen, organization.data, history]);

    return { section: 'users', label: 'Users', isLoading: args.term !== '' && args.isOpen && organization.isLoading, hits };
};

const useVendorsSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const can = useCan(ORG_MODIFY);
    const enabled = args.isOpen && can;
    const vendors = useGetAllVendorSettingsQuery(undefined, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!args.term || !enabled) return [];
        const out: IGlobalSearchHit[] = [];
        for (const vendor of vendors.data?.items ?? []) {
            const label = vendor.vendorDisplayName || vendor.sourceVendor;
            const rank = matchRank(args.term, label, vendor.sourceVendor);
            if (rank === undefined) continue;
            out.push({
                section: 'vendors',
                key: vendor.sourceVendor,
                label,
                secondary: vendor.enabled === false ? 'Disabled' : undefined,
                icon: 'Manufacturing',
                rank,
                // Vendor Management reads this URL-panel contract (useUrlPanelState('vendor')).
                run: () => history.push(`/settings/vendors?panel=vendor&instance=${encodeURIComponent(vendor.sourceVendor)}`),
            });
        }
        return sortHits(out, CAP_ENTITY);
    }, [args.term, enabled, vendors.data, history]);

    return { section: 'vendors', label: 'Vendors', isLoading: args.term !== '' && enabled && vendors.isLoading, hits };
};

const useTaxCodesSection = (args: ISectionArgs): ISectionResult => {
    const can = useCan(TAX_CODE_MODIFY);
    const enabled = args.isOpen && can;
    const taxCodes = useGetTaxCodesQuery(undefined, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!args.term || !enabled) return [];
        const out: IGlobalSearchHit[] = [];
        for (const taxCode of taxCodes.data ?? []) {
            const rank = matchRank(args.term, taxCode.name, taxCode.id, taxCode.description);
            if (rank === undefined) continue;
            out.push({
                section: 'taxcodes',
                key: taxCode.id ?? taxCode.name,
                label: taxCode.name,
                secondary: taxCode.description,
                icon: 'Calculator',
                rank,
                run: () => args.openEntity({ kind: 'taxCode', taxCode }),
            });
        }
        return sortHits(out, CAP_ENTITY);
    }, [args.term, enabled, taxCodes.data, args.openEntity]);

    return { section: 'taxcodes', label: 'Tax Codes', isLoading: args.term !== '' && enabled && taxCodes.isLoading, hits };
};

const useCategoriesSection = (args: ISectionArgs): ISectionResult => {
    const enabled = args.isOpen && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const categories = useSearchProductCategoryRefsQuery({ searchTerm: args.debouncedTerm, pageSize: CAP_ENTITY }, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!enabled) return [];
        return (categories.data?.items ?? []).slice(0, CAP_ENTITY)
            .filter((category) => category.identifier)
            .map((category): IGlobalSearchHit => ({
                section: 'categories',
                key: category.id,
                label: category.label ?? category.identifier,
                secondary: 'Category',
                icon: 'RowsGroup',
                rank: RANK_LABEL_SUBSTRING,
                run: () => args.openEntity({
                    kind: 'productCategory',
                    categoryIdentifier: category.identifier,
                    segmentIdentifier: category.segmentId ?? '',
                }),
            }));
    }, [enabled, categories.data, args.openEntity]);

    return { section: 'categories', label: 'Categories', isLoading: enabled && categories.isFetching, hits };
};

const useSegmentsSection = (args: ISectionArgs): ISectionResult => {
    const enabled = args.isOpen && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const segments = useSearchProductSegmentRefsQuery({ searchTerm: args.debouncedTerm, pageSize: CAP_ENTITY }, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!enabled) return [];
        return (segments.data?.items ?? []).slice(0, CAP_ENTITY)
            .filter((segment) => segment.identifier)
            .map((segment): IGlobalSearchHit => ({
                section: 'segments',
                key: segment.id ?? segment.identifier ?? '',
                label: segment.label ?? segment.identifier ?? '',
                secondary: 'Segment',
                icon: 'GroupedList',
                rank: RANK_LABEL_SUBSTRING,
                run: () => args.openEntity({ kind: 'productSegment', segmentIdentifier: segment.identifier ?? '' }),
            }));
    }, [enabled, segments.data, args.openEntity]);

    return { section: 'segments', label: 'Segments', isLoading: enabled && segments.isFetching, hits };
};

const useOrdersSection = (args: ISectionArgs): ISectionResult => {
    const can = useCan(STOREFRONT_ADMIN);
    const enabled = args.isOpen && can && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const orders = useSearchStorefrontOrderRefsQuery({ searchTerm: args.debouncedTerm, pageSize: CAP_ENTITY }, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!enabled) return [];
        return (orders.data?.items ?? []).slice(0, CAP_ENTITY).map((order): IGlobalSearchHit => ({
            section: 'orders',
            key: order.id,
            label: `Order #${order.orderNumber}`,
            secondary: [order.emailAddress, order.status].filter(Boolean).join(' · '),
            icon: 'ActivateOrders',
            rank: RANK_LABEL_SUBSTRING,
            // Opens the order details panel in place — the same panel the orders grid uses.
            run: () => args.openEntity({ kind: 'order', orderId: order.id }),
        }));
    }, [enabled, orders.data, args.openEntity]);

    return { section: 'orders', label: 'Orders', isLoading: enabled && orders.isFetching, hits };
};

const useCartsSection = (args: ISectionArgs): ISectionResult => {
    const can = useCan(STOREFRONT_ADMIN);
    const enabled = args.isOpen && can && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const carts = useSearchStorefrontCartRefsQuery({ searchTerm: args.debouncedTerm, pageSize: CAP_ENTITY }, { skip: !enabled });
    const preparedCarts = useSearchPreparedCartRefsQuery({ searchTerm: args.debouncedTerm, pageSize: CAP_ENTITY }, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!enabled) return [];
        const out: IGlobalSearchHit[] = [];
        for (const prepared of (preparedCarts.data?.items ?? []).slice(0, CAP_ENTITY)) {
            out.push({
                section: 'carts',
                key: `prepared-${prepared.id}`,
                label: prepared.name ?? prepared.id,
                secondary: 'Prepared cart',
                icon: PREPARED_CART_ICON,
                rank: RANK_LABEL_SUBSTRING,
                // A prepared cart's id is its workspace id — the panel keys off the workspace.
                run: () => args.openEntity({ kind: 'preparedCart', workspaceId: prepared.id }),
            });
        }
        for (const cart of (carts.data?.items ?? []).slice(0, CAP_ENTITY)) {
            out.push({
                section: 'carts',
                key: `cart-${cart.id}`,
                label: cart.emailAddress || cart.id,
                secondary: 'Customer cart',
                icon: 'ShoppingCart',
                rank: RANK_LABEL_SUBSTRING,
                run: () => args.openEntity({ kind: 'cart', cartId: cart.id }),
            });
        }
        return out.slice(0, CAP_ENTITY * 2);
    }, [enabled, carts.data, preparedCarts.data, args.openEntity]);

    return {
        section: 'carts',
        label: 'Carts / Prepared Carts',
        isLoading: enabled && (carts.isFetching || preparedCarts.isFetching),
        hits,
    };
};

const useBundlesSection = (args: ISectionArgs): ISectionResult => {
    const dispatch = useDispatch();
    const can = useCan(SEARCH);
    const enabled = args.isOpen && can && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const bundles = useGetTopProductBundleRefsQuery({ keywords: args.debouncedTerm }, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!enabled) return [];
        return (bundles.data?.items ?? []).slice(0, CAP_ENTITY)
            .filter((bundle) => bundle.id)
            .map((bundle): IGlobalSearchHit => ({
                section: 'bundles',
                key: bundle.id ?? '',
                label: bundle.name ?? bundle.id ?? '',
                secondary: bundle.partNumber,
                icon: BUNDLE_ICON,
                rank: RANK_LABEL_SUBSTRING,
                // Bundles have no route; the app-level BundleModalHost reacts to this redux field
                // and opens the editor over whatever page the user is on.
                run: () => dispatch(setOpenBundleId(bundle.id)),
            }));
    }, [enabled, bundles.data, dispatch]);

    return { section: 'bundles', label: 'Product Bundles', isLoading: enabled && bundles.isFetching, hits };
};

const useSavedSearchesSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const dispatch = useDispatch();
    const can = useCan(SEARCH);
    const enabled = args.isOpen && can;
    const savedSearches = useGetSavedSearchRefsQuery(undefined, { skip: !enabled });

    const hits = React.useMemo(() => {
        if (!args.term || !enabled) return [];
        const out: IGlobalSearchHit[] = [];
        for (const saved of savedSearches.data ?? []) {
            if (!saved.id || !saved.name) continue;
            const rank = matchRank(args.term, saved.name);
            if (rank === undefined) continue;
            // Primary row opens the saved search's details panel on /search (URL-addressable);
            // the companion row applies it — the redux field the Search page reacts to, same as
            // the saved-search selector panel's Apply button.
            out.push({
                section: 'savedSearches',
                key: saved.id,
                label: saved.name,
                secondary: saved.shared ? 'Shared saved search' : 'Saved search',
                icon: 'SingleBookmark',
                rank,
                run: () => history.push(`/search?panel=savedSearch&instance=${encodeURIComponent(saved.id ?? '')}`),
            });
            out.push({
                section: 'savedSearches',
                key: `apply-${saved.id}`,
                label: `Apply ${saved.name}`,
                secondary: 'Run this saved search',
                icon: 'SearchBookmark',
                // Half a tier below the details row so "Apply X" never alphabetically
                // outranks "X" itself on the rank tie-break.
                rank: rank + 0.5,
                run: () => {
                    dispatch(updateSelectedSaveSearchId(saved.id));
                    history.push('/search');
                },
            });
        }
        return sortHits(out, CAP_ENTITY * 2);
    }, [args.term, enabled, savedSearches.data, dispatch, history]);

    return { section: 'savedSearches', label: 'Saved Searches', isLoading: args.term !== '' && enabled && savedSearches.isLoading, hits };
};

/**
 * The shorthand passthrough product search: "> gaming laptop cat:Laptops mfr:Lenovo ram=16GB"
 * (or any query containing key:value sugar) becomes one row that dispatches real search criteria
 * and lands on /search. With the ">" sigil the palette shows only this row.
 */
const useProductSearchSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const dispatch = useDispatch();
    const can = useCan(SEARCH);
    const parsedForStorefronts = parseProductQuery(args.rawTerm);
    // storefront:"Name" sugar resolves to ids at dispatch time — the org list is RTK-cached.
    const storefronts = useGetStorefrontsQuery(undefined, {
        skip: !args.isOpen || parsedForStorefronts.storefrontNames.length === 0,
    });

    // Pseudonym attribute expansion needs the facet catalog. This subscription mirrors the
    // palette's facet preview argument exactly (debounced raw term, same catalog), so RTK serves
    // both from one cache entry — no extra request.
    const selectedCatalog = useSelector((state: RootState) => state.search.selectedCatalog);
    const debouncedParsed = React.useMemo(() => parseProductQuery(args.debouncedRawTerm.trim()), [args.debouncedRawTerm]);
    const facetPreviewEnabled = args.isOpen && can && debouncedParsed.sigil
        && !!(debouncedParsed.criteria.category || debouncedParsed.criteria.subCategory || (debouncedParsed.criteria.manufacturer?.length ?? 0) > 0);
    const facetPreview = useGetSearchFacetPreviewQuery(
        { catalog: selectedCatalog?.name, criteria: buildFacetPreviewCriteria(debouncedParsed.criteria) },
        { skip: !facetPreviewEnabled }
    );
    const facets = facetPreviewEnabled ? facetPreview.data?.metadata?.facets : undefined;

    // Context (a workspace or a bundle): real product rows (not just the hand-off) with an in-place
    // "Add" to it, and for workspaces a ranked suggestion list while nothing has been typed yet.
    const destination = contextDestination(args.context);
    const workspaceId = args.context?.kind === 'workspace' ? args.context.workspaceId : undefined;
    const inContext = !!destination;
    const contextHasQuery = debouncedParsed.sigil && (!!debouncedParsed.keywords || debouncedParsed.summary.length > 0);
    const preview = useGetProductSearchPreviewQuery(
        {
            catalog: selectedCatalog?.name,
            criteria: {
                inStock: true, manufacturer: [], vendor: [], must: [], boost: [], exclude: [], filters: [],
                ...debouncedParsed.criteria,
                page: 1, pageSize: CAP_CONTEXT_PRODUCTS
            }
        },
        { skip: !args.isOpen || !can || !inContext || !contextHasQuery }
    );
    const liveParsed = React.useMemo(() => parseProductQuery(args.rawTerm), [args.rawTerm]);
    const bundleId = args.context?.kind === 'bundle' ? args.context.bundleId : undefined;
    const suggestionsWanted = inContext && liveParsed.sigil && !liveParsed.keywords && liveParsed.summary.length === 0;
    const workspaceSuggestions = useGetWorkspaceProductSuggestionsQuery(
        { workspaceId: workspaceId ?? '', catalog: selectedCatalog?.name ?? '', limit: CAP_CONTEXT_SUGGESTIONS },
        { skip: !args.isOpen || !can || !suggestionsWanted || !workspaceId || !selectedCatalog?.name }
    );
    const bundleSuggestions = useGetBundleProductSuggestionsQuery(
        { bundleId: bundleId ?? '', catalog: selectedCatalog?.name ?? '', limit: CAP_CONTEXT_SUGGESTIONS },
        { skip: !args.isOpen || !can || !suggestionsWanted || !bundleId || !selectedCatalog?.name }
    );
    // Whichever container the palette was opened from supplies the suggestions.
    const suggestions = workspaceId ? workspaceSuggestions : bundleSuggestions;
    const addToContext = React.useCallback(async (catalog: string, productId: string) => {
        if (!destination) return false;
        // The helper only needs id + catalog for either destination type.
        return addProductToDestination(dispatch, destination, { id: productId, catalog } as INormalizedProduct);
    }, [destination?.type, destination?.id, dispatch]);

    const hits = React.useMemo(() => {
        const parsed = parseProductQuery(args.rawTerm);
        if (!parsed.active || !can) return [];
        if (parsed.criteria.boost?.length) {
            // ram=32gb fans out into boosts on every real attribute holding a normalized match.
            parsed.criteria.boost = expandAttributeBoosts(parsed.criteria.boost, facets);
        }
        if (parsed.storefrontNames.length > 0) {
            const wanted = parsed.storefrontNames.map((n) => n.toLowerCase());
            const ids = (storefronts.data?.items ?? [])
                .filter((sf) => wanted.some((w) => (sf.name ?? '').toLowerCase() === w || (sf.subUrl ?? '').toLowerCase() === w))
                .map((sf) => sf.id ?? '')
                .filter(Boolean);
            if (ids.length) parsed.criteria.storefrontFilter = ids;
        }
        const label = parsed.keywords ? `Search products for "${parsed.keywords}"` : 'Search products';
        const contextRows: IGlobalSearchHit[] = [];
        if (inContext) {
            const productRow = (catalog: string, id: string, name: string, secondary: string | undefined, icon: string, rank: number, preview?: IHitPreview): IGlobalSearchHit => ({
                section: 'productSearch',
                key: `ctx-${catalog}-${id}`,
                label: name,
                secondary,
                icon,
                rank,
                preview,
                run: () => args.openEntity({ kind: 'product', productId: id, destination }),
                keepOpen: true,
                secondaryRun: () => addToContext(catalog, id),
                secondaryLabel: 'Add',
                secondaryDoneLabel: 'Added',
            });
            if (contextHasQuery) {
                (preview.data?.products ?? []).slice(0, CAP_CONTEXT_PRODUCTS).forEach((product, index) => {
                    if (!product.id) return;
                    const meta = [product.manufacturer, product.manufacturerPartNumber].filter(Boolean).join(' · ');
                    const range = costRangeOf(product.vendors);
                    const cost = formatCostRange(range.lowest, range.highest);
                    const secondary = [cost, meta].filter(Boolean).join(' · ');
                    const stock = range.inStockVendors > 0
                        ? `In stock at ${range.inStockVendors} vendor${range.inStockVendors === 1 ? '' : 's'}`
                        : (product.vendors?.length ? 'No vendor in stock' : undefined);
                    const hitPreview: IHitPreview = {
                        title: product.name ?? product.id,
                        image: product.image,
                        lines: [meta, cost, stock, product.msrp ? `MSRP ${COST_FORMAT.format(product.msrp)}` : undefined,
                            [product.segment, product.category].filter(Boolean).join(' › ')].filter((l): l is string => !!l),
                        // The search page's synthetic spec line (RAM, CPU, storage, screen, …); raw copy only as a fallback.
                        description: trimDescription(generateShortDescription(product as INormalizedProduct)) ?? trimDescription(product.description),
                    };
                    contextRows.push(productRow(product.catalog || selectedCatalog?.name || '', product.id, product.name ?? product.id, secondary || undefined, 'Product', index, hitPreview));
                });
            } else if (suggestionsWanted) {
                (suggestions.data ?? []).forEach((suggestion, index) => {
                    const cost = formatCostRange(suggestion.lowestCost, suggestion.highestCost);
                    const secondary = [cost, suggestion.detail].filter(Boolean).join(' · ');
                    const meta = [suggestion.manufacturer, suggestion.manufacturerPartNumber].filter(Boolean).join(' · ');
                    const hitPreview: IHitPreview = {
                        title: suggestion.name ?? suggestion.productId,
                        image: suggestion.image,
                        lines: [meta, cost, suggestion.msrp ? `MSRP ${COST_FORMAT.format(suggestion.msrp)}` : undefined, suggestion.detail]
                            .filter((l): l is string => !!l),
                        description: trimDescription(generateShortDescription({ facets: suggestion.facets ?? {} } as INormalizedProduct)),
                    };
                    contextRows.push(productRow(suggestion.catalog, suggestion.productId, suggestion.name ?? suggestion.productId,
                        secondary || undefined, SUGGESTION_ICONS[suggestion.reason] ?? 'Product', index, hitPreview));
                });
            }
        }
        const hit: IGlobalSearchHit = {
            section: 'productSearch',
            key: 'passthrough',
            label: inContext ? `${label} on the full search page` : label,
            secondary: parsed.summary.join(' · ') || undefined,
            icon: 'Search',
            rank: 0,
            run: () => {
                dispatch(updateSearchCriteria({
                    // Passthrough searches default to in-stock; anystock sugar overrides via
                    // parsed.criteria.inStock = false.
                    inStock: true,
                    manufacturer: [],
                    vendor: [],
                    must: [],
                    boost: [],
                    exclude: [],
                    filters: [],
                    ...parsed.criteria,
                }));
                dispatch(updateSelectedSaveSearchId(undefined));
                history.push('/search');
            },
        };
        // In workspace context the hand-off is the escape hatch; the products come first.
        return inContext ? [...contextRows, hit] : [hit];
    }, [args.rawTerm, can, dispatch, history, storefronts.data, facets, inContext, workspaceId, destination, contextHasQuery, preview.data, suggestionsWanted, suggestions.data, addToContext, selectedCatalog?.name, args.openEntity]);

    const isLoading = inContext && ((contextHasQuery && preview.isFetching) || (suggestionsWanted && suggestions.isFetching));
    const sectionLabel = suggestionsWanted ? (bundleId ? 'Suggested for this bundle' : 'Suggested for this workspace') : 'Product Search';
    return { section: 'productSearch', label: sectionLabel, isLoading, hits };
};

const useProductsSection = (args: ISectionArgs): ISectionResult => {
    const history = useHistory();
    const dispatch = useDispatch();
    const can = useCan(SEARCH);
    const enabled = args.isOpen && can && args.debouncedTerm.length >= MIN_SERVER_TERM;
    const quicksearch = useGetQuicksearchQuery({ value: args.debouncedTerm }, { skip: !enabled });
    // Workspace context: quicksearch rows can be added in place too. Quicksearch only knows the
    // id, so the add resolves against the selected catalog (the same one the palette searches).
    const destination = contextDestination(args.context);
    const selectedCatalog = useSelector((state: RootState) => state.search.selectedCatalog);

    const hits = React.useMemo(() => {
        if (!args.term || !can) return [];
        const out: IGlobalSearchHit[] = [];
        if (enabled) {
            for (const [name, id] of Object.entries(quicksearch.data?.productNamesWithIds ?? {}).slice(0, CAP_PRODUCTS)) {
                const catalog = selectedCatalog?.name;
                out.push({
                    section: 'products',
                    key: id,
                    label: name,
                    icon: 'Product',
                    rank: RANK_LABEL_SUBSTRING,
                    run: () => args.openEntity({ kind: 'product', productId: id, destination }),
                    ...(destination && catalog ? {
                        keepOpen: true,
                        secondaryRun: () => addProductToDestination(dispatch, destination, { id, catalog } as INormalizedProduct),
                        secondaryLabel: 'Add',
                        secondaryDoneLabel: 'Added',
                    } : {}),
                });
            }
        }
        // Hand-off to the real search page, always last. /search reads criteria from redux.
        out.push({
            section: 'products',
            key: 'keyword-search',
            label: `Search products for "${args.term}"`,
            icon: 'Search',
            rank: RANK_LABEL_SUBSTRING,
            run: () => {
                dispatch(updateSearchCriteria({
                    keywords: args.term,
                    inStock: false,
                    manufacturer: [],
                    vendor: [],
                    must: [],
                    boost: [],
                    exclude: [],
                    filters: [],
                }));
                dispatch(updateSelectedSaveSearchId(undefined));
                history.push('/search');
            },
        });
        return out;
    }, [args.term, args.openEntity, can, enabled, quicksearch.data, dispatch, history, destination?.type, destination?.id, selectedCatalog?.name]);

    return { section: 'products', label: 'Products', isLoading: enabled && quicksearch.isFetching, hits };
};

/**
 * Aggregates every palette section, in render order. One unconditional hook call per section
 * (rules of hooks — same posture as useInstanceSearchSources); sections gate themselves via
 * permission checks and RTK `skip`, and empty sections are filtered out here.
 *
 * A section-name phrase in the query ("UPS saved search") is a hint: the hinted section matches
 * on the stripped term ("ups") and renders first, ahead of every other section.
 *
 * Only sections that actually have hits are returned — a section whose query is still in flight
 * must not flash its heading in and out of the list (an empty answer is the common case for most
 * sections on most queries). `isSearching` carries the in-flight signal for the palette to show
 * one global indicator instead.
 */
export interface ISearchSectionsResult {
    sections: ISectionResult[];
    isSearching: boolean;
}

export const useSearchSections = (args: ISectionArgs): ISearchSectionsResult => {
    // ">" makes the query a pure product passthrough: every other section goes quiet (no rows,
    // no server queries) and the single passthrough row is the whole palette.
    const isPassthrough = args.rawTerm.startsWith('>');
    const sectionArgs = isPassthrough ? { ...args, term: '', debouncedTerm: '' } : args;
    const productSearch = useProductSearchSection(args);

    const hint = parseSectionHint(sectionArgs.term);
    const debouncedHint = parseSectionHint(sectionArgs.debouncedTerm);
    const hintedSection = hint.section ?? debouncedHint.section;
    const argsFor = (section: SectionKey): ISectionArgs =>
        hintedSection === section
            ? {
                ...sectionArgs,
                term: hint.section === section ? hint.stripped : sectionArgs.term,
                debouncedTerm: debouncedHint.section === section ? debouncedHint.stripped : sectionArgs.debouncedTerm,
            }
            : sectionArgs;

    const actions = useActionsSection(sectionArgs);
    const frequent = useFrequentSearchesSection(sectionArgs);
    const settings = useSettingsSection(argsFor('settings'));
    const storefronts = useStorefrontsSection(argsFor('storefronts'));
    const workspaces = useWorkspacesSection(argsFor('workspaces'));
    const contacts = useContactsSection(argsFor('contacts'));
    const companies = useCompaniesSection(argsFor('companies'));
    const users = useUsersSection(argsFor('users'));
    const extensions = useExtensionsSection(argsFor('extensions'));
    const vendors = useVendorsSection(argsFor('vendors'));
    const taxCodes = useTaxCodesSection(argsFor('taxcodes'));
    const categories = useCategoriesSection(argsFor('categories'));
    const segments = useSegmentsSection(argsFor('segments'));
    const orders = useOrdersSection(argsFor('orders'));
    const carts = useCartsSection(argsFor('carts'));
    const bundles = useBundlesSection(argsFor('bundles'));
    const savedSearches = useSavedSearchesSection(argsFor('savedSearches'));
    const products = useProductsSection(argsFor('products'));

    return React.useMemo(() => {
        if (isPassthrough) {
            return { sections: productSearch.hits.length > 0 ? [productSearch] : [], isSearching: productSearch.isLoading };
        }
        const all = [productSearch, actions, frequent, settings, storefronts, workspaces, contacts, companies, users, extensions, vendors, taxCodes, categories, segments, orders, carts, bundles, savedSearches, products];
        const ordered = all.filter((section) => section.hits.length > 0);
        const isSearching = all.some((section) => section.isLoading);
        if (!hintedSection) return { sections: ordered, isSearching };
        // The hinted section jumps the queue — "UPS saved search" puts Saved Searches first.
        return {
            sections: [
                ...ordered.filter((section) => section.section === hintedSection),
                ...ordered.filter((section) => section.section !== hintedSection),
            ],
            isSearching,
        };
    }, [productSearch, actions, frequent, settings, storefronts, workspaces, contacts, companies, users, extensions, vendors, taxCodes, categories, segments, orders, carts, bundles, savedSearches, products, hintedSection, isPassthrough]);
};
